<template>
  <div class="border border-gray-200 rounded-lg p-4">
    <p v-if="description" class="text-xs text-gray-500 mb-3">{{ description }}</p>

    <!-- Uploaded files list -->
    <div v-for="(file, index) in modelValue" :key="index" class="flex items-center gap-2 text-sm text-gray-600 mb-1">
      <CheckCircleIcon class="h-4 w-4 text-green-500 shrink-0" />
      <span class="truncate">{{ file.name }}</span>
      <button type="button" @click="remove(index)" class="text-red-400 hover:text-red-600 shrink-0">
        <XMarkIcon class="h-3.5 w-3.5" />
      </button>
    </div>

    <p v-if="fileValidationError" class="mt-1 mb-2 text-xs text-red-600">
      {{ fileValidationError }}
    </p>

    <label class="mt-2 inline-flex items-center gap-1.5 cursor-pointer rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-500">
      <DocumentArrowUpIcon class="h-4 w-4" />
      {{ $gettext("Ajouter un fichier") }}
      <input type="file" class="sr-only" :accept="allowedDocumentTypeStore.acceptString" @change="handleNewFile" />
    </label>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { DocumentArrowUpIcon, CheckCircleIcon, XMarkIcon } from "@heroicons/vue/24/outline";
import { useGettext } from "vue3-gettext";
import { useAllowedDocumentTypeStore } from "@/stores/allowedDocumentType";

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  description: { type: String, default: null },
});

const emit = defineEmits(["update:modelValue"]);
const { $gettext } = useGettext();
const allowedDocumentTypeStore = useAllowedDocumentTypeStore();
const fileValidationError = ref(null);

// Same client-side guard as FormDocumentsField: Shrine rejects disallowed
// extensions and oversized files, and here the file would only reach the server
// at the very end of the wizard — long after this screen.
const handleNewFile = (event) => {
  const file = event.target.files[0];
  if (!file) return;
  event.target.value = "";

  fileValidationError.value = null;

  const ext = file.name.split(".").pop().toLowerCase();
  const maxSize = allowedDocumentTypeStore.maxSizeFor(ext);
  if (!allowedDocumentTypeStore.allowedExtensions.includes(ext)) {
    fileValidationError.value = $gettext("Le fichier n'est pas autorisé. Seuls les fichiers de type %{extensions} sont autorisés.", { extensions: allowedDocumentTypeStore.allowedExtensions.join(", ") });
    return;
  }
  if (maxSize && file.size > maxSize * 1024 * 1024) {
    fileValidationError.value = $gettext("Le fichier est trop grand. La taille maximale autorisée est de %{maxSize} Mo.", { maxSize: maxSize });
    return;
  }

  emit("update:modelValue", [...props.modelValue, file]);
};

const remove = (index) => {
  const updated = [...props.modelValue];
  updated.splice(index, 1);
  emit("update:modelValue", updated);
};
</script>
